// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import FacilityContext from "./FacilityContext";
import type { Facility, FacilityMember, FacilityRole } from "./FacilityContext";

const ACTIVE_FACILITY_KEY = "camp_promethean_active_facility";

/** Active facility id for non-React callers (zustand stores, utilities).
 *  Components should use useFacility().activeFacility instead. */
export function getActiveFacilityId(): string | null {
  return localStorage.getItem(ACTIVE_FACILITY_KEY);
}

export function FacilityProvider({ children }: { children: React.ReactNode }) {
  const { profile, isAuthenticated, loading: authLoading } = useAuth();
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [members, setMembers] = useState<FacilityMember[]>([]);
  const [activeFacilityId, setActiveFacilityId] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_FACILITY_KEY)
  );
  const [loading, setLoading] = useState(true);

  const fetchFacilities = useCallback(async () => {
    if (!profile?.id) {
      setFacilities([]);
      setMembers([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data: membershipData, error: membershipError } = await supabase
        .from('facility_members')
        .select('*')
        .eq('user_id', profile.id);

      if (membershipError) throw membershipError;

      const facilityIds = (membershipData || []).map(m => m.facility_id);

      if (facilityIds.length === 0) {
        setFacilities([]);
        setMembers([]);
        setLoading(false);
        return;
      }

      const { data: facilityData, error: facilityError } = await supabase
        .from('facilities')
        .select('*')
        .in('id', facilityIds)
        .order('name');

      if (facilityError) throw facilityError;

      const typedFacilities: Facility[] = (facilityData || []).map((f: any) => ({
        id: f.id,
        name: f.name,
        slug: f.slug ?? null,
        invite_code: f.invite_code ?? null,
        invite_enabled: f.invite_enabled ?? true,
        owner_id: f.owner_id ?? null,
        user_id: f.user_id,
        timezone: f.timezone ?? null,
        created_at: f.created_at,
        updated_at: f.updated_at,
      }));

      setFacilities(typedFacilities);
      setMembers(membershipData || []);

      setActiveFacilityId((current) => {
        const stored = localStorage.getItem(ACTIVE_FACILITY_KEY);
        const candidate =
          current && facilityIds.includes(current)
            ? current
            : stored && facilityIds.includes(stored)
            ? stored
            : null;

        if (candidate) {
          localStorage.setItem(ACTIVE_FACILITY_KEY, candidate);
          return candidate;
        }
        if (typedFacilities.length === 1) {
          const id = typedFacilities[0].id;
          localStorage.setItem(ACTIVE_FACILITY_KEY, id);
          return id;
        }
        localStorage.removeItem(ACTIVE_FACILITY_KEY);
        return null;
      });
    } catch (err) {
      console.error("Error fetching facilities:", err);
      setFacilities([]);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    // Auth resolves asynchronously on mount; don't wipe stored selection while it loads.
    if (authLoading) return;

    if (isAuthenticated && profile?.id) {
      fetchFacilities();
    } else {
      setFacilities([]);
      setMembers([]);
      setActiveFacilityId(null);
      localStorage.removeItem(ACTIVE_FACILITY_KEY);
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, profile?.id, fetchFacilities]);

  const switchFacility = useCallback((facilityId: string) => {
    setActiveFacilityId(facilityId);
    localStorage.setItem(ACTIVE_FACILITY_KEY, facilityId);
  }, []);

  const activeFacility = facilities.find(f => f.id === activeFacilityId) || null;

  const userRole: FacilityRole | null = activeFacilityId
    ? (members.find(m => m.facility_id === activeFacilityId)?.role as FacilityRole) || null
    : null;

  const canWrite = userRole
    ? ['owner', 'admin', 'manager', 'member'].includes(userRole)
    : false;

  const isAdmin = userRole
    ? ['owner', 'admin'].includes(userRole)
    : false;

  return (
    <FacilityContext.Provider
      value={{
        activeFacility,
        facilities,
        userRole,
        members,
        loading,
        switchFacility,
        refreshFacilities: fetchFacilities,
        canWrite,
        isAdmin,
      }}
    >
      {children}
    </FacilityContext.Provider>
  );
}

export default FacilityProvider;
